import React, { useState, useCallback, useRef, useEffect } from 'react';
import {Link } from 'react-router-dom';
import io from 'socket.io-client';
import '../css/card_demo.css';
import axiosInstance from '../utils/axiosConfig';
import CardNewsGenerator from './CardNewsGenerator';
import { useUsage } from '../contexts/UsageContext';

const apiUrl = process.env.REACT_APP_API_URL;


// 페르소나 설정을 위한 데이터 구조 정의
const DATA = {
  // 성별 옵션
  genderOptions: [
    { value: 'male', label: '남성' },
    { value: 'female', label: '여성' }
  ],
  
  // 연령대 옵션
  ageOptions: [
    { value: '30', label: '30대' },
    { value: '40', label: '40대' },
    { value: '50', label: '50대' },
    { value: '60', label: '60대' }
  ],
};

  // 이미지 스타일 옵션 (value: API 전송용, label: UI 표시용)
  const IMAGE_STYLE_OPTIONS = [
    { value: "Studio ghibli", label: "지브리 스타일"},
    { value: "watercolor drawing", label: "수채화 드로잉" },
    { value: "cartoon style illustration", label: "카툰 스타일 일러스트" },
    { value: "flat design infographic", label: "플랫 디자인 인포그래픽" },
    { value: "paper cutout style", label: "종이 오리기 스타일" },
    { value: "watercolor storybook", label: "수채화 동화책" },
    { value: "3D isometric style", label: "3D 아이소메트릭 스타일" },
    { value: "pixel art", label: "픽셀 아트" },
    { value: "pop art watercolor", label: "팝 아트 수채화" },
    { value: "flat minimal design", label: "플랫 미니멀 디자인" },
    { value: "low-poly illustration", label: "로우 폴리 일러스트" }
  ];

// 이미지 모달 컴포넌트
const Modal = ({ imageUrl, onClose }) => {
  // 모달 외부 클릭 시 닫기 처리
  const handleModalClick = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleModalClick}>
      <div className="modal-content">
        <img src={imageUrl} alt="확대된 이미지" className="modal-image" />
      </div>
    </div>
  );
};

const CardNews = () => {
  // 사용량 갱신
  const { refreshUsage } = useUsage();

  // 상태 관리
  const [selectedCard, setSelectedCard] = useState(null);        // 선택된 카드 이미지
  const [gender, setGender] = useState('');                      // 선택된 성별
  const [age, setAge] = useState('');                           // 선택된 연령대
  const [scenario, setScenario] = useState('');                 // 선택된 시나리오
  const [isGenerating, setIsGenerating] = useState(false);      // 텍스트 생성 중 상태
  const [messages, setMessages] = useState([]);                  // 생성된 메시지 목록
  const [textProcessSteps, setTextProcessSteps] = useState([]); // 텍스트 생성 프로세스 단계
  const [isGeneratingImages, setIsGeneratingImages] = useState(false); // 이미지 생성 중 상태
  const [imageError, setImageError] = useState(null);           // 이미지 생성 오류
  const [generatedImages, setGeneratedImages] = useState([]);   // 생성된 이미지 목록
  const [processStepImg, setProcessStepImg] = useState([]);     // 이미지 생성 프로세스 단계
  const [imageStyle, setImageStyle] = useState(''); // 이미지 스타일 상태

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const previewImageUrl = "/img/preview.svg"; // 미리보기할 이미지 URL

  // 시나리오 데이터 불러오기 & 시나리오 옵션 동적 생성
  const [loading, setLoading] = useState(true);
  const [scenarios, setScenarios] = useState([]);
  const [error, setError] = useState(null);
  const [scenarioOptions, setScenarioOptions] = useState([]);  // scenarioOptions를 위한 state 추가
  const [scenarioId, setScenarioId] = useState('');  // 시나리오 ID를 위한 state 추가
  const [additionalExplanation, setAdditionalExplanation] = useState('');

  const fetchScenarios = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/scenarios`);
      setScenarios(response.data);
      
      // ID를 포함하여 options 생성
      const options = response.data.map(scenario => ({
        value: scenario.scenario_case_en,
        label: scenario.scenario_case_ko,
        id: scenario.scenario_id  // ID 추가
      }));
      
      setScenarioOptions(options);
      // console.log("scenario options >> ", options);
    } catch (err) {
      setError('시나리오 데이터를 불러오는데 실패했습니다.');
      console.error('Error fetching scenarios:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenarios();
  }, []);

  // 시나리오 선택 핸들러 수정
  const handleScenarioChange = (selectedValue) => {
    const selectedOption = scenarioOptions.find(option => option.value === selectedValue);
    if (selectedOption) {
      setScenario(selectedOption.value);
      setScenarioId(selectedOption.id);  // ID 저장
    }
  };


  // refs for scrolling
  const messageRef = useRef(null);
  const imageRef = useRef(null);

  // 스크롤 헬퍼 함수
  const scrollToBottom = (ref) => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  };

  // 메시지나 이미지가 추가될 때 자동 스크롤
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => scrollToBottom(messageRef), 100);
    }
  }, [messages]);
  
  useEffect(() => {
    if (generatedImages.length > 0) {
      setTimeout(() => scrollToBottom(imageRef), 100);
    }
  }, [generatedImages]);

  // 이미지 스타일 변경 감지 (디버깅용)
  useEffect(() => {
    // console.log('[이미지 스타일 변경] 새 값:', imageStyle);
  }, [imageStyle]);

  // 텍스트 생성 함수
  const handleTextGeneration = useCallback(async () => {
    // if (!gender || !age || !scenario) return;
    if (!gender || !age || !scenario || !scenarioId) return;

    // 이미지 스타일 확인 (디버깅용)
    // console.log('현재 선택된 이미지 스타일:', imageStyle);

    // 이미지 스타일 미선택 시 확인
    if (!imageStyle) {
      const confirmGeneration = window.confirm(
        '이미지 스타일이 선택되지 않았습니다.\n' +
        '랜덤으로 이미지 스타일이 생성됩니다.\n\n' +
        '그래도 진행하시겠습니까?'
      );

      if (!confirmGeneration) {
        return; // 사용자가 "아니오"를 선택한 경우 중단
      }
    }

    // 프로세스 단계 정의
    const processSteps = [
      { id: 1, title: '뉴스 데이터 반영', status: 'pending' },
      { id: 2, title: 'AI 텍스트 생성', status: 'pending' },
      { id: 3, title: '컨텐츠 검증', status: 'pending' },
      { id: 4, title: '민감어휘 필터링', status: 'pending' }
    ];

    try {
      // 초기 상태 설정
      setIsGenerating(true);
      setTextProcessSteps([]);
      setMessages([]);

      // API 호출 시작
      const apiCall = axiosInstance.post(`/generate_text`, {
        age: age,
        gender: gender,
        scenario: scenario,
        scenario_id: scenarioId,  // ID 추가
        additionalExplanation: additionalExplanation
      });

      // 프로세스 단계 순차적 표시
      for (let i = 0; i < processSteps.length; i++) {
        const currentSteps = processSteps.slice(0, i + 1).map(step => ({
          ...step,
          status: 'completed'
        }));
        
        setTextProcessSteps(currentSteps);
        await new Promise(resolve => setTimeout(resolve, 4500));
      }

      // API 응답 처리
      const response = await apiCall;
      if (response.data.versions) {
        for (let i = 0; i < response.data.versions.length; i++) {
          await new Promise(resolve => setTimeout(resolve, 800));
          setMessages(prev => {
            const newMessages = [...prev, response.data.versions[i]];
            setTimeout(() => scrollToBottom(messageRef), 100);
            return newMessages;
          });
        }
      }

    } catch (error) {
      console.error('텍스트 생성 에러:', error);
      alert('텍스트 생성 중 오류가 발생했습니다.');
    } finally {
      setIsGenerating(false);
    }
  // }, [gender, age, scenario]);
  }, [gender, age, scenario, scenarioId, additionalExplanation, imageStyle]);  // imageStyle 의존성 추가

  // 이미지 생성 함수 (DALLE 사용)
  const handleImageGenerationDalle = async () => {
    if (!messages.length) return;
    
    // 이미지 생성 프로세스 단계 정의
    const processSteps = [
      { id: 1, title: '프롬프트 구성', status: 'pending' },
      { id: 2, title: '스타일 지정', status: 'pending' },
      { id: 3, title: '이미지 생성', status: 'pending' },
    ];

    try {
      // 초기 상태 설정
      setIsGeneratingImages(true);
      setImageError(null);
      setGeneratedImages([]);
      setProcessStepImg([]);

      // 사용량 먼저 갱신하여 화면에 표시
      await refreshUsage();

      // API 호출 준비
      // const apiCall = axios.post(`${apiUrl}/gen_image_mid`, {
      // const apiCall = axios.post(`${apiUrl}/gen_image_dalle`, {
      //   persona: { gender, age, scenario },
      //   messages: messages.map(msg => ({
      //     title: msg.title,
      //     subtitle: msg.subtitle,
      //     description: msg.description
      //   }))
      // }, {
      //   timeout: 600000 // 10분 타임아웃
      // });

      const apiCall = axiosInstance.post(`/gen_image_dalle`, {
        
      // const apiCall = axios.post(`${apiUrl}/gen_image_mid`, {
        persona: { 
          gender, 
          age, 
          scenario,
          imageStyle, // 선택된 이미지 스타일 (영문 데이터)
          additionalExplanation,
          scenario_case_ko: scenarioOptions.find(opt => opt.value === scenario)?.label 
        },
        messages: messages.map(msg => ({
          title: msg.title,
          subtitle: msg.subtitle,
          description: msg.description
        }))
      }, {
        timeout: 600000
      });

      // console.log(">>>>> dalle >>>>>")
      // 프로세스 단계 순차적 표시
      for (let i = 0; i < processSteps.length; i++) {
        const currentSteps = processSteps.slice(0, i + 1).map(step => ({
          ...step,
          status: 'completed'
        }));
        
        setProcessStepImg(currentSteps);
        await new Promise(resolve => setTimeout(resolve, 7000));
      }

      // API 응답 처리
      const response = await apiCall;
      if (response.data.status === 200) {
        const imagePaths = response.data.image_paths || response.data.images || [];
        
        if (imagePaths.length > 0) {
          // 이미지 순차적 표시
          for (let i = 0; i < imagePaths.length; i++) {
            await new Promise(resolve => setTimeout(resolve, 500));
            setGeneratedImages(prev => [...prev, imagePaths[i]]);
            scrollToBottom(imageRef);
          }
          // 사용량 갱신
          refreshUsage();
        } else {
          setImageError('이미지를 받지 못했습니다.');
        }
      } else {
        setImageError('서버 응답이 올바르지 않습니다.');
      }
      
    } catch (error) {
      console.error('Error:', error);
      // 서버 에러 메시지에 따른 사용자 친화적 메시지 표시
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.message;

      if (errorCode === 'LIMIT_EXCEEDED' || errorMessage?.includes('limit exceeded')) {
        setImageError('오늘의 이미지 생성 할당량이 모두 소진되었습니다.\n내일 다시 시도해 주세요.');
      } else {
        setImageError(error.response?.data?.error || errorMessage || '이미지 생성 중 오류가 발생했습니다.');
      }
    } finally {
      setIsGeneratingImages(false);
    }
  };

  

  // 옵션 선택기 컴포넌트
  const OptionSelector = ({ label, options, value, onChange }) => {
    return (
      <div className="persona-section">
        <h4>{label}</h4>
        <select
          value={value}
          onChange={(e) => {
            // console.log(`[${label}] 선택됨:`, e.target.value);
            onChange(e.target.value);
          }}
          className="persona-dropdown"
        >
          <option value="">선택하세요</option>
          {options.map((option, index) => (
            <option key={option.id || option.value || `option-${index}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  };

  // 메인 렌더링
  return (
    <div className="container">
      <header className="App-header">
        <h1>페르소나 기반 고객 맞춤형 AI 컨텐츠 생성 솔루션</h1>
        
        {/* 대시보드 링크 */}
        {/* <Link to="/card_dashboard" className="dashboard-button">
          Dashboard
        </Link> */}
        <button 
          onClick={() => window.open('/card_news_reserve', '_blank', 'noopener,noreferrer')} 
          className="dashboard-button"
        >
          예약생성
        </button>
        <button 
          onClick={() => window.open('/card_dashboard', '_blank', 'noopener,noreferrer')} 
          className="dashboard-button"
        >
          대시보드
        </button>

        <button 
          onClick={() => window.open('/scenario_management', '_blank', 'noopener,noreferrer')} 
          className="dashboard-button"
        >
          시나리오
        </button>
        
      </header>

      <div className="App-columns">
        {/* 페르소나 설정 섹션 */}
        <div className="App-column">
          <h2>Process 1</h2>
          <h3>페르소나 설정</h3>
          
          <div className="column-content">
            <OptionSelector 
              label="성별" 
              options={DATA.genderOptions} 
              value={gender} 
              onChange={setGender}
            />
            <OptionSelector 
              label="연령대" 
              options={DATA.ageOptions} 
              value={age} 
              onChange={setAge}
            />
            <OptionSelector 
              label="시나리오" 
              // options={DATA.scenarioOptions} 
              options={scenarioOptions} 
              value={scenario} 
              // onChange={setScenario}
              onChange={handleScenarioChange}  // 수정된 핸들러 사용
            />

            {/* 이미지 스타일 선택 버튼 그룹 */}
            <OptionSelector
                label="이미지 스타일 선택"
                options={IMAGE_STYLE_OPTIONS}
                value={imageStyle}
                onChange={setImageStyle}
              />
              <button onClick={() => setIsPreviewOpen(true)}>
                이미지 미리보기
              </button>

            {/* 추가 설명 입력란 */}
            {/* <div className="persona-section">
              <h4>고객 특성 입력</h4>
              <textarea
                value={additionalExplanation}
                onChange={(e) => setAdditionalExplanation(e.target.value)}
                className="persona-textarea"
                placeholder="견종, 차량, 취미 등"
                rows={4}
              />
            </div> */}

          </div>

          <button
            onClick={handleTextGeneration}
            disabled={!gender || !age || !scenario || isGenerating}
            className="action-button"
          >
            텍스트 생성하기
          </button>
        </div>

        {/* 텍스트 생성 결과 섹션 */}
        <div className="App-column">
          <h2>Process 2</h2>
          <h3 className={isGenerating ? 'processing-title animate-pulse' : ''}>
            {isGenerating ? 'AI 텍스트 생성 중...' : 'AI 텍스트 생성'}
          </h3>
          
          <div className="column-content">
            {/* 프로세스 단계 표시 */}
            <div className="process-steps">
              {textProcessSteps.map((step, index) => (
                <div key={index} className={`process-step ${step.status === 'completed' ? 'completed' : ''}`}>
                  <div className="step-number">{step.id}</div>
                  <div className="step-title">{step.title}</div>
                  <div className="step-status">
                    {step.status === 'completed' ? '완료' : '대기'}
                  </div>
                </div>
              ))}
            </div>
            
            {/* 생성된 메시지 표시 */}
            <div className="text-result-container message-container" ref={messageRef}>
              {messages.map((message, index) => (
                <div key={index} className="message-section fade-in">
                  <h4 className="message-number">메시지 {index + 1}</h4>
                  <div className="message-content">
                    <p><strong>제목:</strong> {message.title}</p>
                    <p><strong>부제목:</strong> {message.subtitle}</p>
                    <p><strong>설명문:</strong> {message.description}</p>
                  </div>
                  {index < messages.length - 1 && <hr />}
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleImageGenerationDalle}
            disabled={messages.length === 0 || isGeneratingImages}
            className="action-button"
          >
            이미지 생성하기
          </button>
        </div>

        {/* 이미지 생성 결과 섹션 */}
        <div className="App-column">
          <h2>Process 3</h2>
          <h3 className={isGeneratingImages ? 'processing-title' : ''}>
            {isGeneratingImages ? 'AI 이미지 생성 중' : 'AI 이미지 생성'}
          </h3>

          {/* 이미지 생성 프로세스 단계 */}
          <div className="process-steps">
            {processStepImg.map((step, index) => (
              <div key={index} className={`process-step ${step.status === 'completed' ? 'completed' : ''}`}>
                <div className="step-number">{step.id}</div>
                <div className="step-title">{step.title}</div>
                <div className="step-status">
                  {step.status === 'completed' ? '완료' : '대기'}
                </div>
              </div>
            ))}
          </div>

          {/* 에러 메시지 표시 */}
          {imageError && (
            <div className="error-message">
              {imageError}
            </div>
          )}

          {/*생성된 이미지 표시 */}
          <div className="column-content">
            <div className="img-result-container" ref={imageRef}>
              <div className="image-grid">
                {generatedImages.map((imageUrl, index) => {
                  // 페이지 번호와 하위 번호 계산
                  const pageNum = Math.floor(index / 4) + 1;
                  const subNum = (index % 4) + 1;

                  return (
                    <div key={index} className="image-item">
                      <img
                        src={imageUrl}
                        alt={`Generated ${pageNum}-${subNum}`}
                        className="grid-image"
                        onClick={() => setSelectedCard(imageUrl)}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/placeholder.jpg';
                        }}
                      />
                      <span className="image-number">{`${pageNum}-${subNum}`}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* 카드뉴스 생성 섹션 */}
        <div className="App-column">
          <h2>Process 4</h2>
          <CardNewsGenerator 
            messages={messages} 
            generatedImages={generatedImages} 
          />
        </div>
      </div>

      {/* 이미지 확대 모달 */}
      {selectedCard && (
        <Modal
          imageUrl={selectedCard}
          onClose={() => setSelectedCard(null)}
        />
      )}

        {/* JSX의 마지막 부분에 Modal을 간단하게 추가 */}
        {isPreviewOpen && (
          <div 
            className="modal-overlay" 
            onClick={() => setIsPreviewOpen(false)}
            style={{
              position: "fixed",
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <div 
              className="modal-content" 
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                padding: "1rem",
                borderRadius: "4px",
                position: "relative"
              }}
            >
              <img src={previewImageUrl} alt="미리보기" style={{ maxWidth: "100%" }} />
              <button 
                onClick={() => setIsPreviewOpen(false)}
                style={{
                  position: "absolute",
                  top: "0.5rem",
                  right: "0.5rem"
                }}
              >
                닫기
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default CardNews;